import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  PLAN_RESULT_LABEL,
  RAG_LABEL,
  cnDate,
  fmtDateWeek,
  fmtMonthDay,
  type PlanResult,
  type ProgramDTO,
  type ProgramPlanItem,
  type ProgramRag,
  type ProgramReportDTO,
  type ProgramReportRow,
  type ProgramTaskDTO,
} from '@wd/shared';
import { errorText } from '../../lib/api';
import { timeCN } from '../../lib/clock';
import { useMembers, useProgramActions, useProgramReport, useReportEdit } from '../../lib/queries';
import { Icon, MoreIcon, StatusIcon } from '../../ui/icons';
import { MenuList, Popover } from '../../ui/menu';
import { AIAvatar, Button, EmptyState, IconButton, Modal, Quote, Skeleton } from '../../ui/primitives';
import { Editable } from './Editable';
import { SlipTag, grouped, md } from './ProgramTasks';

// 进度汇报（参考 PRINCE2 Highlight Report）：总体状态 → 关键任务进展与延期说明 → 上期计划完成情况 → 下期计划 → 风险、需要决策的事。
// 每一格都能直接改；进展说明上方灰色的是前几期的记录，和项目经理原来在表格里按日期往下记的写法一样。

const RAGS: ProgramRag[] = ['green', 'amber', 'red'];
/** 延后了却还没写原因（空着，或者是起草时留下的「原因待补充」「未说明」） */
const needsReason = (t: ProgramTaskDTO, row: ProgramReportRow | undefined) =>
  t.status !== 'done' && t.status !== 'cancelled' && !!t.slipDays && t.slipDays > 0 && (!row?.delayNote.trim() || /待补充|未说明/.test(row.delayNote));
const RESULTS: PlanResult[] = ['done', 'partial', 'not_done'];
const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export function ReportTab({ program, mobile }: { program: ProgramDTO; mobile: boolean }) {
  const [params, setParams] = useSearchParams();
  const fallback = (program.reports.find((r) => r.status === 'draft') ?? program.reports[0])?.id ?? null;
  const reportId = program.reports.some((r) => r.id === params.get('report')) ? params.get('report') : fallback;
  const { data: r, isLoading, error } = useProgramReport(reportId);
  const actions = useProgramActions(program.id);
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  const pick = (id: string) =>
    setParams(
      (p) => {
        p.set('report', id);
        return p;
      },
      { replace: true },
    );
  const draft = () =>
    actions.draft.mutate(undefined, {
      onSuccess: (rep) => {
        pick(rep.id);
        setToast('已起草，逐格核对后发送');
      },
      onError: (e) => setToast(errorText(e)),
    });

  if (actions.draft.isPending)
    return (
      <div className="pg-report" aria-busy="true">
        <div className="t-meta">正在根据上一期之后的日报、评论和日期变更起草，大约需要半分钟…</div>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ marginTop: 24 }}>
            <Skeleton w={120} h={10} />
            <Skeleton w="90%" h={12} style={{ marginTop: 12 }} />
          </div>
        ))}
      </div>
    );

  if (!reportId)
    return (
      <EmptyState
        icon="report"
        title="还没有进度汇报"
        desc={
          program.tasks.length
            ? 'AI 根据最近一周关键任务的日报、评论和日期变更起草：本期进展、延期说明、下期计划和风险，项目经理逐格核对后发送。'
            : '先在「关键任务」里把任务加进来，再起草汇报。'
        }
      >
        {program.canEdit && program.tasks.length > 0 && (
          <Button variant="primary" onClick={draft}>
            起草第一期汇报
          </Button>
        )}
        {toast && <div className="t-meta">{toast}</div>}
      </EmptyState>
    );
  if (error) return <EmptyState icon="wifioff" title="汇报没有加载出来" desc={errorText(error)} />;
  if (isLoading || !r)
    return (
      <div className="pg-report" aria-busy="true">
        <Skeleton w={200} h={18} />
      </div>
    );

  const hasDraft = program.reports.some((x) => x.status === 'draft');
  return <Report program={program} r={r} mobile={mobile} onPick={pick} onDraft={draft} hasDraft={hasDraft} toast={toast} setToast={setToast} />;
}

function Report({
  program,
  r,
  mobile,
  onPick,
  onDraft,
  hasDraft,
  toast,
  setToast,
}: {
  program: ProgramDTO;
  r: ProgramReportDTO;
  mobile: boolean;
  onPick: (id: string) => void;
  onDraft: () => void;
  hasDraft: boolean;
  toast: string | null;
  setToast: (t: string | null) => void;
}) {
  const { edit, send, remove } = useReportEdit(r.id);
  const [confirm, setConfirm] = useState(false);
  const [menu, setMenu] = useState(false);
  const [picker, setPicker] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);
  const pickRef = useRef<HTMLButtonElement>(null);
  const can = r.canEdit;
  const save = (patch: Parameters<typeof edit.mutate>[0]) => edit.mutate(patch, { onError: (e) => setToast(errorText(e)) });
  const recipients = [...program.reportTo, ...(program.sponsor && !program.reportTo.some((x) => x.id === program.sponsor!.id) ? [program.sponsor] : [])];

  const copy = async () => {
    const { html, text } = asTable(program, r);
    try {
      if (window.ClipboardItem && navigator.clipboard?.write)
        await navigator.clipboard.write([new ClipboardItem({ 'text/html': new Blob([html], { type: 'text/html' }), 'text/plain': new Blob([text], { type: 'text/plain' }) })]);
      else await navigator.clipboard.writeText(text);
      setToast('已复制为表格，可以直接粘贴到邮件或 Excel');
    } catch {
      setToast('复制没有成功，请用「导出 PDF」');
    }
  };

  return (
    <div className="pg-report">
      <div className="pg-print-head">
        {program.code} {program.name} · 进度汇报 · {fmtDateWeek(r.asOf)}
      </div>
      <div className="pg-r-head">
        <div className="col" style={{ gap: 4, minWidth: 0 }}>
          <div className="pg-r-title">
            {fmtMonthDay(r.asOf)} 进度汇报
            <span className={clsx('pg-chip', r.status)}>{r.status === 'sent' ? '已发送' : '草稿'}</span>
          </div>
          <div className="t-meta pg-r-sub">
            {r.generatedBy === 'ai' ? <AIAvatar size={12} /> : <Icon name="doc" size={12} />}
            <span>
              本期材料 {md(r.since)} – {md(r.asOf)} · {r.generatedBy === 'ai' ? 'AI 根据日报起草' : '根据日报整理'}
              {can ? ' · 每一格都可以直接改' : ''}
            </span>
          </div>
        </div>
        <span className="flex1" />
        {program.reports.length > 1 && (
          <>
            <button ref={pickRef} type="button" className="wd-btn ghost pg-pick-btn" onClick={() => setPicker(!picker)}>
              往期
              <Icon name="chevD" size={12} />
            </button>
            <Popover anchor={pickRef} open={picker} onClose={() => setPicker(false)} width={220} align="end">
              <MenuList
                items={program.reports.map((x) => ({
                  value: x.id,
                  label: `${fmtMonthDay(x.asOf)} ${x.status === 'draft' ? '草稿' : RAG_LABEL[x.rag]}`,
                  icon: <span className={clsx('pg-rag-dot', x.rag)} />,
                }))}
                selected={r.id}
                onPick={(id) => {
                  setPicker(false);
                  onPick(id);
                }}
              />
            </Popover>
          </>
        )}
        {program.canEdit && (
          <>
            <button ref={moreRef} type="button" className="wd-btn icon" aria-label="更多" onClick={() => setMenu(!menu)}>
              <MoreIcon />
            </button>
            <Popover anchor={moreRef} open={menu} onClose={() => setMenu(false)} width={220} align="end">
              <MenuList
                items={[
                  ...(r.status === 'draft'
                    ? [
                        { value: 'redraft', label: '重新起草（覆盖修改）', icon: <Icon name="refresh" size={14} /> },
                        { value: 'delete', label: '删除这份草稿', icon: <Icon name="minus" size={14} /> },
                      ]
                    : hasDraft
                      ? []
                      : [{ value: 'redraft', label: '起草新一期', icon: <Icon name="plus" size={14} /> }]),
                ]}
                selected={null}
                onPick={(v) => {
                  setMenu(false);
                  if (v === 'redraft') onDraft();
                  if (v === 'delete') remove.mutate(undefined, { onSuccess: () => setToast('草稿已删除') });
                }}
                emptyText="已有一份草稿，先处理它"
              />
            </Popover>
          </>
        )}
      </div>

      {/* 总体状态 */}
      <section className="pg-sec">
        <div className="pg-sec-head">总体情况</div>
        <div className="pg-rag">
          {RAGS.map((g) => (
            <button key={g} type="button" className={clsx('pg-rag-btn', g, r.rag === g && 'on')} disabled={!can} onClick={() => save({ rag: g })}>
              <span className={clsx('pg-rag-dot', g)} />
              {RAG_LABEL[g]}
            </button>
          ))}
          {can && r.rag !== r.ragSuggested && <span className="t-meta">按任务状态和偏差，系统建议「{RAG_LABEL[r.ragSuggested]}」</span>}
        </div>
        <Editable value={r.summary} canEdit={can} placeholder="一句话：整体进度如何，最大的延期或风险是什么" maxLength={300} onSave={(v) => save({ summary: v })} className="pg-summary" />
      </section>

      {/* 关键任务 */}
      <section className="pg-sec">
        <div className="pg-sec-head">关键任务</div>
        <div className={clsx('pg-grid head', mobile && 'mobile')}>
          <span>关键任务</span>
          <span>计划时间</span>
          <span>进展说明</span>
          <span>延期说明</span>
        </div>
        {grouped(program.tasks.filter((t) => t.status !== 'cancelled')).map((g) => (
          <div key={g.section ?? '-'} className="pg-group">
            {g.section && <div className="pg-group-head">{g.section}</div>}
            {g.tasks.map((t) => (
              <TaskLine key={t.projectId} t={t} r={r} row={r.rows.find((x) => x.projectId === t.projectId)} can={can} mobile={mobile} onSave={(patch) => save({ rows: [{ projectId: t.projectId, ...patch }] })} />
            ))}
          </div>
        ))}
      </section>

      {/* 上期计划完成情况 */}
      <section className="pg-sec">
        <div className="pg-sec-head">上期计划完成情况</div>
        {!r.lastPeriod.length ? (
          <div className="pg-none">{program.reports.filter((x) => x.status === 'sent').length ? '上一期没有写下期计划' : '这是第一期汇报，下次起草时会对照这一期的「下期计划」'}</div>
        ) : (
          r.lastPeriod.map((x, i) => (
            <div key={x.id} className="pg-plan">
              <span className="txt">
                <TaskCode program={program} id={x.projectId} />
                {x.text}
                {x.date && <span className="c4"> · {md(x.date)}</span>}
              </span>
              <span className="res">
                {RESULTS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={clsx('pg-res', v, x.result === v && 'on')}
                    disabled={!can}
                    onClick={() => save({ lastPeriod: r.lastPeriod.map((y, j) => (j === i ? { ...y, result: y.result === v ? null : v } : y)) })}
                  >
                    {PLAN_RESULT_LABEL[v]}
                  </button>
                ))}
              </span>
              <Editable
                value={x.note}
                canEdit={can}
                placeholder="说明（没完成的写原因）"
                maxLength={200}
                className="note"
                onSave={(v) => save({ lastPeriod: r.lastPeriod.map((y, j) => (j === i ? { ...y, note: v } : y)) })}
              />
            </div>
          ))
        )}
      </section>

      {/* 下期计划 */}
      <section className="pg-sec">
        <div className="pg-sec-head">下期计划</div>
        <NextPlans program={program} r={r} can={can} onSave={(list) => save({ nextPeriod: list })} />
      </section>

      <section className="pg-sec">
        <div className="pg-sec-head">风险与问题</div>
        <Editable value={r.risks} canEdit={can} multiline placeholder="每条一行：影响哪个任务、可能造成什么后果" maxLength={2000} onSave={(v) => save({ risks: v })} className="pg-block" />
      </section>
      <section className="pg-sec">
        <div className="pg-sec-head">需要决策或支持</div>
        <Editable value={r.asks} canEdit={can} multiline placeholder="每条一行：需要谁、在什么时间前、决定什么" maxLength={2000} onSave={(v) => save({ asks: v })} className="pg-block" />
      </section>

      <div className={clsx('pg-foot', mobile && 'mobile')}>
        {r.status === 'sent' ? (
          <span className="t-meta pg-sent">
            <StatusIcon status="done" />
            已发送 · {fmtMonthDay(cnDate(new Date(r.sentAt!)))} {timeCN(r.sentAt!)}
            {r.sentByName ? ` · ${r.sentByName}` : ''}
          </span>
        ) : (
          can && (
            <Button variant="primary" mobile={mobile} onClick={() => setConfirm(true)}>
              发送给汇报对象
            </Button>
          )
        )}
        <Button icon="copy" mobile={mobile} onClick={() => void copy()}>
          复制为表格
        </Button>
        {!mobile && (
          <Button icon="export" onClick={() => window.print()}>
            导出 PDF
          </Button>
        )}
        <span className="flex1" />
        {toast && <span className="t-meta">{toast}</span>}
      </div>

      {confirm && (
        <Modal
          width={460}
          title="发送进度汇报"
          subtitle={`${program.name} · ${fmtMonthDay(r.asOf)}`}
          onClose={() => setConfirm(false)}
          footNote="发送后存档，不能再修改"
          footer={
            <Button
              variant="primary"
              disabled={send.isPending}
              onClick={() =>
                send.mutate(undefined, {
                  onSuccess: (x) => {
                    setConfirm(false);
                    setToast(x.recipients ? `已发送给 ${x.recipients} 人` : '已存档（还没有设置汇报对象）');
                  },
                  onError: (e) => setToast(errorText(e)),
                })
              }
            >
              发送
            </Button>
          }
        >
          {(() => {
            const missing = program.tasks.filter((t) => needsReason(t, r.rows.find((x) => x.projectId === t.projectId)));
            return missing.length ? (
              <div className="pg-warn">
                这 {missing.length} 个任务延后了，还没写原因：{missing.map((t) => `${t.code}（延后 ${t.slipDays} 天）`).join('、')}。建议补上再发。
              </div>
            ) : null;
          })()}
          <div className="t-body">
            总体状态「{RAG_LABEL[r.rag]}」。
            {recipients.length
              ? `会通知 ${recipients.map((x) => x.name).join('、')}，同时发邮件（邮件里附汇报全文，直接回复会发给项目经理）。`
              : '还没有设置汇报对象，发送后只保存记录。可以在「项目说明」里添加。'}
          </div>
        </Modal>
      )}
    </div>
  );
}

function TaskCode({ program, id }: { program: ProgramDTO; id: string | null }) {
  const t = id ? program.tasks.find((x) => x.projectId === id) : null;
  return t ? <span className="c4">{t.code} </span> : null;
}

function TaskLine({
  t,
  r,
  row,
  can,
  mobile,
  onSave,
}: {
  t: ProgramTaskDTO;
  r: ProgramReportDTO;
  row: ProgramReportRow | undefined;
  can: boolean;
  mobile: boolean;
  onSave: (patch: { progress?: string; delayNote?: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  const history = r.history[t.projectId] ?? [];
  const sources = row?.sources ?? [];
  return (
    <div className={clsx('pg-grid', mobile && 'mobile')}>
      <span className="task">
        <Link to={`/projects/${t.projectId}`} className="name">
          <span className="c4">{t.code}</span> {t.name}
        </Link>
        <span className="t-meta row" style={{ gap: 4 }}>
          <StatusIcon status={t.status} size={12} />
          {t.owner.name}
        </span>
      </span>
      <span className="plan">
        <span>{t.baselineStart || t.baselineEnd ? `${md(t.baselineStart)} – ${md(t.baselineEnd)}` : <span className="c4">未设基线</span>}</span>
        <span className="t-meta">{t.status === 'done' ? `已完成 ${md(t.doneAt)}` : `预计 ${md(t.dueDate)}`}</span>
        <SlipTag t={t} />
      </span>
      <span className="progress">
        {history.map((h) => (
          <span key={h.asOf} className="hist">
            <span className="d">{md(h.asOf)}：</span>
            {h.progress}
          </span>
        ))}
        {!t.visible ? (
          <span className="c4">没有权限查看这个项目的进展</span>
        ) : (
          <span className="cur">
            <span className="d">{md(r.asOf)}：</span>
            <Editable
              value={row?.progress ?? ''}
              canEdit={can}
              multiline
              placeholder="本期做到了哪一步"
              clearOnEdit="本期无进展记录"
              maxLength={600}
              onSave={(v) => onSave({ progress: v })}
            />
            {sources.length > 0 && (
              <button type="button" className="pg-src-btn" onClick={() => setOpen(!open)} aria-expanded={open} title="依据的日报和评论">
                <Icon name="doc" size={12} stroke={1.4} />
                {sources.length}
              </button>
            )}
          </span>
        )}
        {open && (
          <span className="pg-src">
            {sources.map((s, i) => (
              <Quote key={i} meta={[s.memberName ?? '系统', fmtDateWeek(s.date), s.label].join(' · ')}>
                {s.text}
              </Quote>
            ))}
            {row?.edited && <span className="t-meta">已手动修改，依据仅供参考</span>}
          </span>
        )}
      </span>
      <span className={clsx('delay', can && needsReason(t, row) && 'missing')}>
        {can && needsReason(t, row) && <span className="pg-need">延后 {t.slipDays} 天，请写原因</span>}
        {t.visible && (
          <Editable
            value={row?.delayNote ?? ''}
            canEdit={can}
            multiline
            placeholder={t.slipDays && t.slipDays > 0 ? '延期原因和追回计划' : '没有延期可以不写'}
            maxLength={300}
            onSave={(v) => onSave({ delayNote: v })}
          />
        )}
      </span>
    </div>
  );
}

function NextPlans({ program, r, can, onSave }: { program: ProgramDTO; r: ProgramReportDTO; can: boolean; onSave: (list: ProgramPlanItem[]) => void }) {
  const { data: members = [] } = useMembers();
  const list = r.nextPeriod;
  const set = (i: number, patch: Partial<ProgramPlanItem>) => onSave(list.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <>
      {!list.length && !can && <div className="pg-none">没有写下期计划</div>}
      {list.map((x, i) => (
        <div key={x.id} className="pg-next">
          {can ? (
            <select className="pf-select task" value={x.projectId ?? ''} onChange={(e) => set(i, { projectId: e.target.value || null })} aria-label="关联的关键任务">
              <option value="">不关联任务</option>
              {program.tasks.map((t) => (
                <option key={t.projectId} value={t.projectId}>
                  {t.code} {t.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="task c4">{program.tasks.find((t) => t.projectId === x.projectId)?.code ?? ''}</span>
          )}
          <Editable value={x.text} canEdit={can} placeholder="做什么、做到什么程度" maxLength={200} className="txt" onSave={(v) => (v ? set(i, { text: v }) : onSave(list.filter((_, j) => j !== i)))} />
          {can ? (
            <PlanDate value={x.date} onSave={(date) => set(i, { date })} />
          ) : (
            <span className="date c4">{x.date ? md(x.date) : ''}</span>
          )}
          {can ? (
            <select className="pf-select who" value={x.ownerId ?? ''} onChange={(e) => set(i, { ownerId: e.target.value || null })} aria-label="负责人">
              <option value="">负责人</option>
              {members
                .filter((m) => m.status === 'active' || m.status === 'invited')
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
            </select>
          ) : (
            <span className="who c4">{members.find((m) => m.id === x.ownerId)?.name ?? ''}</span>
          )}
          {can && <IconButton name="close" label="删除这条" onClick={() => onSave(list.filter((_, j) => j !== i))} />}
        </div>
      ))}
      {can && (
        <button type="button" className="pg-add" onClick={() => onSave([...list, { id: newId(), text: '新的计划', projectId: null, date: null, ownerId: null, result: null, note: '' }])}>
          <Icon name="plus" size={12} />
          添加一条
        </button>
      )}
    </>
  );
}

/** 键盘逐位输入年份时，输入框会依次变成 0002、0020、0202……：年份四位齐了，或者离开这一格时再保存 */
function PlanDate({ value, onSave }: { value: string | null; onSave: (v: string | null) => void }) {
  const [d, setD] = useState(value ?? '');
  useEffect(() => setD(value ?? ''), [value]);
  const commit = (v: string) => {
    if (v !== (value ?? '')) onSave(v || null);
  };
  return (
    <input
      className="wd-input date"
      type="date"
      value={d}
      onChange={(e) => {
        setD(e.target.value);
        if (/^(19|20)\d\d-/.test(e.target.value)) commit(e.target.value);
      }}
      onBlur={() => commit(d)}
      aria-label="计划完成"
    />
  );
}

// ---------------------------------------------------------------------------
// 复制为表格：粘贴到邮件或 Excel 时保持原来的表格格式
// ---------------------------------------------------------------------------

function asTable(program: ProgramDTO, r: ProgramReportDTO): { html: string; text: string } {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
  const td = (s: string, head = false) =>
    `<${head ? 'th' : 'td'} style="border:1px solid #c8ccd2;padding:6px 8px;vertical-align:top;text-align:left;${head ? 'background:#4472c4;color:#fff;' : ''}">${s}</${head ? 'th' : 'td'}>`;
  const lines: string[] = [];
  const text: string[] = [`${program.name} 进度汇报 ${fmtMonthDay(r.asOf)}`, `总体：${RAG_LABEL[r.rag]}。${r.summary}`, ''];
  lines.push(`<p><b>${esc(program.name)} 进度汇报 ${fmtMonthDay(r.asOf)}</b><br>总体：${RAG_LABEL[r.rag]}。${esc(r.summary)}</p>`);
  lines.push('<table style="border-collapse:collapse;font-size:13px;">');
  lines.push(`<tr>${['分组', '关键任务', '计划时间', '进展说明', '延期说明'].map((h) => td(h, true)).join('')}</tr>`);
  for (const g of grouped(program.tasks.filter((t) => t.status !== 'cancelled')))
    for (const t of g.tasks) {
      const row = r.rows.find((x) => x.projectId === t.projectId);
      const hist = (r.history[t.projectId] ?? []).map((h) => `${md(h.asOf)}：${h.progress}`);
      const cur = row?.progress ? [`${md(r.asOf)}：${row.progress}`] : [];
      const plan = `${md(t.baselineStart)}–${md(t.baselineEnd)}${t.status === 'done' ? ' 已完成' : ''}${t.slipDays && t.slipDays > 0 ? `（延后 ${t.slipDays} 天）` : ''}`;
      lines.push(`<tr>${[g.section ?? '', `${t.code} ${t.name}`, plan, [...hist, ...cur].join('\n'), row?.delayNote ?? ''].map((c) => td(esc(c))).join('')}</tr>`);
      text.push(`${g.section ? `[${g.section}] ` : ''}${t.code} ${t.name}｜${plan}｜${[...hist, ...cur].join('；')}${row?.delayNote ? `｜延期：${row.delayNote}` : ''}`);
    }
  lines.push('</table>');
  const planTable = (title: string, list: ProgramPlanItem[], withResult: boolean) => {
    if (!list.length) return;
    lines.push(`<p><b>${title}</b></p><table style="border-collapse:collapse;font-size:13px;">`);
    lines.push(`<tr>${['序号', '关键任务', withResult ? '计划完成' : '计划节点', withResult ? '完成情况' : '负责人', '说明'].map((h) => td(h, true)).join('')}</tr>`);
    text.push('', title);
    list.forEach((x, i) => {
      const code = program.tasks.find((t) => t.projectId === x.projectId)?.code ?? '';
      const third = withResult ? (x.result ? PLAN_RESULT_LABEL[x.result] : '') : '';
      lines.push(`<tr>${[String(i + 1), `${code} ${x.text}`.trim(), x.date ? md(x.date) : '', third, x.note].map((c) => td(esc(c))).join('')}</tr>`);
      text.push(`${i + 1}. ${code ? `${code} ` : ''}${x.text}${x.date ? `（${md(x.date)}）` : ''}${third ? ` ${third}` : ''}${x.note ? ` ${x.note}` : ''}`);
    });
    lines.push('</table>');
  };
  planTable('上期计划完成情况', r.lastPeriod, true);
  planTable('下期计划', r.nextPeriod, false);
  if (r.risks) {
    lines.push(`<p><b>风险与问题</b><br>${esc(r.risks)}</p>`);
    text.push('', '风险与问题', r.risks);
  }
  if (r.asks) {
    lines.push(`<p><b>需要决策或支持</b><br>${esc(r.asks)}</p>`);
    text.push('', '需要决策或支持', r.asks);
  }
  return { html: lines.join(''), text: text.join('\n') };
}
